/**
 * Dismissal rules for the routing data update notice.
 *
 * Mirrors utils/offlineMapUpdateNotice.js. Shown once per published version,
 * dismissible, and does not return for that version once dismissed.
 *
 * The routing-specific rule: an incompatible package must never prompt an
 * install. There is nothing the user can do about it, and a recurring nag for
 * an action they cannot take is worse than silence. See TCA-26081002.
 */

export const DISMISSED_ROUTING_UPDATE_VERSION_KEY = 'lvb.routingData.dismissedUpdateVersion';
export const DISMISSED_ROUTING_INCOMPATIBLE_KEY = 'lvb.routingData.dismissedIncompatibleVersion';

import { userIsAdmin } from '@/utils/userRoles';

// Re-exported: these modules are the existing import site for it.
export { userIsAdmin };

function readKey(storage, key) {
  if (!storage) return null;
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function writeKey(storage, key, value) {
  if (!storage || !value) return false;
  try {
    storage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

export function getDismissedRoutingUpdateVersion(storage) {
  return readKey(storage, DISMISSED_ROUTING_UPDATE_VERSION_KEY);
}

export function dismissRoutingUpdateVersion(version, storage) {
  return writeKey(storage, DISMISSED_ROUTING_UPDATE_VERSION_KEY, version);
}

export function getDismissedRoutingIncompatibleVersion(storage) {
  return readKey(storage, DISMISSED_ROUTING_INCOMPATIBLE_KEY);
}

export function dismissRoutingIncompatibleVersion(version, storage) {
  return writeKey(storage, DISMISSED_ROUTING_INCOMPATIBLE_KEY, version);
}

/**
 * Decide what, if anything, to show.
 *
 * Returns 'update' when a newer installable package exists, 'incompatible'
 * when one exists that this app cannot read, and null otherwise.
 */
export function routingNoticeToShow({
  updateStatus,
  user,
  dismissedUpdateVersion,
  dismissedIncompatibleVersion,
  desktopApp,
} = {}) {
  if (!desktopApp || !userIsAdmin(user)) return null;
  if (!updateStatus?.checked) return null;

  const publishedVersion = updateStatus?.published?.version;
  if (!publishedVersion || !updateStatus?.updateAvailable) return null;

  if (updateStatus.compatible === false) {
    // Shown once, worded as an explanation rather than a prompt. The user
    // cannot install it and the app auto-updates, so the only remedy is the
    // developer.
    return publishedVersion === dismissedIncompatibleVersion ? null : 'incompatible';
  }

  return publishedVersion === dismissedUpdateVersion ? null : 'update';
}
